/**
 * THE ONE BUTTON SYSTEM.
 *
 * `docs/UI-INTERACTIONS.md`, ordered by feel-per-unit-of-work: "One shared
 * `Button` control with press feedback (95% and spring back) and a disabled
 * state. Every control routes through it. Doing this per screen is how half of
 * them end up without it."
 *
 * The owner's 2026-10-14 ask raised the bar to AAA: "buttons, elements, views,
 * animations, button animations and sounds, icons for everything". So this file
 * is now the whole vocabulary, in three entry points:
 *
 *   createButton(opts)   build a new button: skin, icon slot, label, press
 *                        activation, sound, loading spinner, success tick.
 *   buttonClass(v, s)    the class string, for a site that must keep building
 *                        its own `<button>` (a test that asserts its markup,
 *                        a form submit) and only needs the skin.
 *   skinButton(node, o)  apply the skin + sound to an EXISTING control in one
 *                        call — the migration path for hand-rolled buttons.
 *
 * ── VARIANTS ────────────────────────────────────────────────────────────────
 *   primary    the gold call to action. One per view; it is what "press this"
 *              looks like and nothing else may wear the gold fill.
 *   secondary  the raised slate plate — the default, every ordinary action.
 *   ghost      a quiet text-weight action beside a primary ("Skip", "Later").
 *   danger     destructive: red ink, red rim, never a red FILL (a red fill
 *              shouts the same as gold and the two would compete).
 *   icon       a square 44px module carrying only an icon slot; MUST be
 *              given an aria-label.
 *   pill       a compact rounded chip — filters, tags, quick actions.
 *
 * ── DEPTH ───────────────────────────────────────────────────────────────────
 * Four layers, matching app.css's elevation ladder (the note there explains
 * why a blurred drop shadow cannot carry depth on the #151A21 ground): a face
 * gradient, a top gloss band, a solid offset LIP the press sinks onto, and a
 * soft contact shadow under the lip. The rim is the 2px --rga-rim.
 *
 * ── MOTION, AND THE TRANSFORM CONTRACT ──────────────────────────────────────
 * Every button still carries `rga-press`, which owns `transform` through the
 * --rga-press-y / --rga-press-s variables (the contract note in press.ts —
 * errata E2 — and test/ui-motion.test.ts enforce it). The SQUASH this system
 * adds rides on the independent CSS `scale` PROPERTY instead, which composes
 * with `transform` rather than competing with it, so the press law stays
 * intact: held = sink onto the lip + 95% + squash wide-and-short; released =
 * a 260ms overshoot spring back (`is-released`, set by attachPress). Under
 * prefers-reduced-motion the squash and the spring go; the sink stays, instant.
 *
 * ── WHY THE SHEET IS IN @layer components ───────────────────────────────────
 * Tailwind v4 declares `@layer theme, base, components, utilities`. A skin in
 * `components` LOSES to the call site's own utilities (`mt-3 w-full px-3`) and
 * to every unlayered screen sheet (`.rga-shop-tile__buy` and friends), which is
 * exactly the precedence a shared skin should have: the screen can always
 * specialise it, and nothing has to fight a specificity war to do so. The
 * press/plate contract stays unlayered in press.ts and controls.ts.
 * (No backticks inside BUTTON_CSS: it is a template literal.)
 */
import { el } from '../layout/dom';

import { createSlotIcon, isIconSlot, type IconSlot } from './icon-registry';
import { PRESS_CSS, attachPress } from './press';
import { BUTTON_CSS, BUTTON_STYLE_MARK } from './button-css';
import { ensureUiStyles } from './styles';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'icon' | 'pill';
export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * The UI sound a press plays. Mirrors `UiSoundId` in `engine/audio/ui-sounds`
 * without importing it, so the ui layer carries no audio dependency; the
 * delegated listener in `engine/audio/cues.ts` reads `data-sfx`.
 */
export type ButtonSound =
  | 'click'
  | 'confirm'
  | 'toggle'
  | 'tab'
  | 'error'
  | 'purchase'
  | 'reward'
  | 'coin'
  | 'modal_open'
  | 'modal_close'
  | 'none';

export const BUTTON_VARIANTS: readonly ButtonVariant[] = Object.freeze([
  'primary',
  'secondary',
  'ghost',
  'danger',
  'icon',
  'pill',
]);

export interface ButtonOptions {
  /** Visible text. Omit and pass `content` for a non-text button. */
  readonly label?: string;
  /** Children, when the button is more than a string. */
  readonly content?: readonly (Node | string)[];
  /** A named icon slot (`engine/ui/icon-registry`). Leads the label. */
  readonly icon?: IconSlot;
  /** Put the icon after the label instead (e.g. "Next →"). */
  readonly iconEnd?: boolean;
  /**
   * Omit for a `submit` button: the form's own submit path is the activation,
   * and the factory then supplies press FEEDBACK only. Every other button
   * must pass one.
   */
  readonly onPress?: () => void;
  /** `submit` inside a form; `button` (the default) everywhere else. */
  readonly type?: 'button' | 'submit';
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Override the variant's default press sound. */
  readonly sound?: ButtonSound;
  /** Extra utility classes — per-site margins and widths stay at the call site. */
  readonly class?: string;
  /** `data-rga` hook, kept identical to whatever the screen used before. */
  readonly dataRga?: string;
  /** `data-testid` hook. */
  readonly testId?: string;
  readonly ariaLabel?: string;
  readonly disabled?: boolean;
  /**
   * The button is drawn over a `pointer-events-none` layer above the match
   * canvas, so it must opt its own events back in. The coach overlay is the
   * one case in the game today.
   */
  readonly overlay?: boolean;
  readonly reducedMotion?: boolean;
}

export interface Button {
  readonly root: HTMLButtonElement;
  setLabel(text: string): void;
  setDisabled(disabled: boolean): void;
  /**
   * Busy: a spinner replaces the content (the width is kept, so the row does
   * not jump), the control is disabled and `aria-busy` is set. A second press
   * during a request is the classic double-purchase bug; this makes it
   * physically impossible.
   */
  setLoading(loading: boolean): void;
  /** A ~900ms tick over the label, then back. For "saved", "claimed", "copied". */
  flashSuccess(): void;
  destroy(): void;
}

/**
 * The recipe distilled from the five hand-rolled copies this file replaced,
 * plus the skin. `min-h-11 min-w-11` is 44 CSS px — the WCAG 2.2 target-size
 * floor, per STYLE_BIBLE §8 and `TAP_TARGET` in `engine/layout/dom`.
 */
const BASE = 'rga-press rga-btn inline-flex min-h-11 min-w-11 items-center justify-center';

/** The default sound per variant: the gold CTA confirms, danger warns. */
const VARIANT_SOUND: Readonly<Record<ButtonVariant, ButtonSound>> = Object.freeze({
  primary: 'confirm',
  secondary: 'click',
  ghost: 'click',
  danger: 'click',
  icon: 'click',
  pill: 'click',
});

/** The class string for a skinned button. Safe to concatenate with utilities. */
export function buttonClass(
  variant: ButtonVariant = 'secondary',
  size: ButtonSize = 'md',
  extra?: string,
): string {
  const parts = [BASE, `rga-btn--${variant}`, `rga-btn--${size}`];
  if (extra !== undefined && extra.length > 0) parts.push(extra);
  return parts.join(' ');
}

export interface SkinOptions {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly sound?: ButtonSound;
}

/**
 * Put the skin and the sound on a control a screen already built. Adds classes
 * only — never removes the screen's own — so a test that asserted the old
 * markup still finds it. Activation is NOT attached here: a site migrating
 * through this function must already route through `attachPress`.
 */
export function skinButton<T extends HTMLElement>(node: T, options: SkinOptions = {}): T {
  ensureUiStyles(node.ownerDocument);
  ensureButtonStyles(node.ownerDocument);
  const variant = options.variant ?? 'secondary';
  for (const c of buttonClass(variant, options.size ?? 'md').split(' ')) node.classList.add(c);
  node.setAttribute('data-sfx', options.sound ?? VARIANT_SOUND[variant]);
  return node;
}

/** Wrap a label so loading/success can hide it without losing the width. */
function labelNode(text: string): HTMLSpanElement {
  return el('span', { class: 'rga-btn__label', text });
}

/** Build a button. The returned handle owns its listeners; call `destroy`. */
export function createButton(options: ButtonOptions): Button {
  ensureUiStyles();
  ensureButtonStyles();

  const variant = options.variant ?? 'secondary';
  const classes = [
    options.overlay === true ? 'pointer-events-auto' : null,
    buttonClass(variant, options.size ?? (variant === 'pill' ? 'sm' : 'md')),
    options.class,
  ]
    .filter((c): c is string => typeof c === 'string' && c.length > 0)
    .join(' ');

  const icon =
    options.icon !== undefined && isIconSlot(options.icon)
      ? createSlotIcon(document, options.icon, {
          size: options.size === 'lg' ? 26 : options.size === 'sm' ? 18 : 22,
          class: 'rga-btn__icon',
        })
      : null;
  const label = options.label === undefined ? null : labelNode(options.label);
  const body: (Node | string)[] = [];
  if (icon !== null && options.iconEnd !== true) body.push(icon);
  if (label !== null) body.push(label);
  if (options.content !== undefined) body.push(...options.content);
  if (icon !== null && options.iconEnd === true) body.push(icon);

  const root = el(
    'button',
    {
      type: options.type ?? 'button',
      class: classes,
      'data-sfx': options.sound ?? VARIANT_SOUND[variant],
      ...(options.dataRga === undefined ? {} : { 'data-rga': options.dataRga }),
      ...(options.testId === undefined ? {} : { 'data-testid': options.testId }),
      ...(options.ariaLabel === undefined ? {} : { 'aria-label': options.ariaLabel }),
      ...(options.disabled === true ? { disabled: true } : {}),
    },
    body,
  );

  const teardown = attachPress(root, {
    onPress: () => options.onPress?.(),
    ...(options.reducedMotion === undefined ? {} : { reducedMotion: options.reducedMotion }),
  });

  let loading = false;
  let wantDisabled = options.disabled === true;
  let successTimer: ReturnType<typeof setTimeout> | null = null;

  const syncDisabled = (): void => {
    root.disabled = wantDisabled || loading;
  };

  return {
    root,
    setLabel(text) {
      if (label !== null) label.textContent = text;
      else root.textContent = text;
    },
    setDisabled(disabled) {
      wantDisabled = disabled;
      syncDisabled();
    },
    setLoading(next) {
      if (next === loading) return;
      loading = next;
      root.classList.toggle('is-loading', next);
      if (next) {
        root.setAttribute('aria-busy', 'true');
        root.append(el('span', { class: 'rga-btn__spinner', 'aria-hidden': 'true' }));
      } else {
        root.removeAttribute('aria-busy');
        root.querySelector(':scope > .rga-btn__spinner')?.remove();
      }
      syncDisabled();
    },
    flashSuccess() {
      if (successTimer !== null) clearTimeout(successTimer);
      root.querySelector(':scope > .rga-btn__tick')?.remove();
      root.classList.add('is-success');
      root.append(el('span', { class: 'rga-btn__tick', 'aria-hidden': 'true' }));
      successTimer = setTimeout(() => {
        successTimer = null;
        root.classList.remove('is-success');
        root.querySelector(':scope > .rga-btn__tick')?.remove();
      }, SUCCESS_MS);
    },
    destroy() {
      if (successTimer !== null) clearTimeout(successTimer);
      teardown();
    },
  };
}

/** How long the success tick holds before the label returns. */
export const SUCCESS_MS = 900;

/* ══════════════════════════════════════════════════════════════ the skin ══ */
/* The sheet itself lives in button-css.ts, import-free, so styles.ts can
   inject it without a cycle. */

/** Inject the skin once per document. `ensureUiStyles` also does this. */
export function ensureButtonStyles(doc: Document = document): void {
  ensureUiStyles(doc);
}

export { BUTTON_CSS, BUTTON_STYLE_MARK };

/** The press half, for a caller that only wants that sheet. */
export const PRESS_ONLY_CSS = PRESS_CSS;

/* ══════════════════════════════════════════════════ async: no double submits ══ */

const busyNodes = new WeakSet<HTMLElement>();

/**
 * Run an async action from ANY existing control with the button system's busy
 * state: disabled + `aria-busy` + spinner for as long as the promise is out,
 * and a second press while it is out is IGNORED (returns `undefined` without
 * calling `action`). The classic double-purchase / double-claim bug, closed
 * for a hand-built `<button>` the same way `createButton().setLoading` closes
 * it for a factory one.
 *
 * The control's own `disabled` is restored to what it was before, so a
 * screen that disabled it for its own reason keeps that reason.
 */
export async function runBusy<T>(node: HTMLElement, action: () => Promise<T>): Promise<T | undefined> {
  if (busyNodes.has(node)) return undefined;
  busyNodes.add(node);
  const isButton = node instanceof HTMLButtonElement;
  const wasDisabled = isButton ? node.disabled : node.getAttribute('aria-disabled') === 'true';
  if (isButton) node.disabled = true;
  else node.setAttribute('aria-disabled', 'true');
  node.setAttribute('aria-busy', 'true');
  node.classList.add('is-loading');
  const spinner = el('span', { class: 'rga-btn__spinner', 'aria-hidden': 'true' });
  // The spinner is positioned by `.rga-btn`; on an unskinned control it is a
  // harmless empty span, and `is-loading` still dims the control.
  if (node.classList.contains('rga-btn')) node.append(spinner);
  try {
    return await action();
  } finally {
    busyNodes.delete(node);
    spinner.remove();
    node.classList.remove('is-loading');
    node.removeAttribute('aria-busy');
    if (isButton) node.disabled = wasDisabled;
    else if (wasDisabled) node.setAttribute('aria-disabled', 'true');
    else node.removeAttribute('aria-disabled');
  }
}

/** True while `runBusy` holds this control. */
export function isBusy(node: HTMLElement): boolean {
  return busyNodes.has(node);
}
